// Ask AVRASTRO (spec §110): a local, deterministic question engine for Greek and English. It maps a
// question to a saved segment, a report or a draft action, and always returns the supporting
// records. Calculations come from the domain engines; nothing leaves the device. (An LLM provider
// can be added behind the same interface once the owner connects one — see docs.)
import {
  addDays, latinize, productionReport, renewalQueue, runSegment, startOfWeek, isActivePolicy, annualNow, isTerminalStage, reviewStatus, type Day, type PeriodKind, type SegmentKey, type WorkspaceView,
} from '@vero/domain';
import { opportunityRadar } from './radar.ts';
import { coverageProfile } from './coverage.ts';
import { relationshipHealth } from './health.ts';

export type AskIntent =
  | { kind: 'segment'; segment: SegmentKey }
  | { kind: 'renewals'; from: Day; to: Day }
  | { kind: 'draft'; personId: string; template: string }
  | { kind: 'campaign_draft'; segment: SegmentKey }
  | { kind: 'production'; period: PeriodKind }
  | { kind: 'start_my_day' }
  | { kind: 'client_summary'; personId: string }
  | { kind: 'meeting_prep'; personId: string }
  | { kind: 'opportunities' }
  | { kind: 'unknown' };
export interface AskResult { intent: AskIntent; answerKey: string; params: Record<string, string | number>; people: { personId: string; detail: Record<string, string | number | boolean> }[]; ambiguous?: string[] }

const SEGMENT_RULES: [RegExp, SegmentKey][] = [
  [/(life|ζωη[ς]?).*(no|without|χωρις|οχι).*(medical|health|ιατρικ|υγει)/, 'life_no_medical'],
  [/(education|σπουδ|εκπαιδευ)/, 'parents_no_education'],
  [/(under ?10|below ?10|κατω (απο |των )?10|<\s*10)/, 'parents_children_under_10'],
  [/(0|zero).{0,3}12|(under|κατω).{0,8}12|παιδια.*12/, 'parents_children_0_12'],
  [/(mortgage|στεγαστ).*(credit|protection|προστασ|ζωης)|(credit life)/, 'mortgage_no_credit_life'],
  [/(no|without|χωρις).*(medical|health|ιατρικ)/, 'no_medical_recorded'],
  [/(retire|pension|συνταξ)/, 'over_40_no_retirement'],
  [/(saving|αποταμιευ)/, 'life_no_savings'],
  [/(birthday|γενεθλ)/, 'birthdays_this_month'],
  [/(name ?day|γιορτ|ονομαστ)/, 'namedays_this_week'],
  [/(tax|φορο)/, 'tax_opportunity'],
  [/(lapsed|ληξ|ανενεργ|σταματ)/, 'lapsed_policies'],
  [/(review|αναθεωρ)/, 'reviews_overdue'],
];

/** best-effort person lookup by name, accent- and script-insensitive */
function findPeople(v: WorkspaceView, q: string): { id: string; name: string }[] {
  const words = q.split(' ').filter((w) => w.length >= 3);
  const out: { id: string; name: string; score: number }[] = [];
  for (const p of v.rows('persons')) {
    if (p.archivedAt) continue;
    const n = latinize(p.displayName);
    const parts = n.split(' ');
    const score = words.filter((w) => parts.includes(w) || parts.some((x) => x.startsWith(w) && w.length >= 4)).length;
    if (score) out.push({ id: p.id, name: p.displayName, score });
  }
  const best = Math.max(0, ...out.map((x) => x.score));
  return out.filter((x) => x.score === best);
}

export function parseQuestion(v: WorkspaceView, question: string, today: Day): { intent: AskIntent; ambiguous?: string[] } {
  const q = latinize(question);
  const g = question.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase(); // Greek, accent-free (regex rules are written in both scripts)
  const both = `${q} ${g}`;
  if (/(draft|write|prepare|γραψε|ετοιμασε|συνταξε).*(follow|message|email|μηνυμα|email)/.test(both) || /^(draft|γραψε)/.test(both)) {
    const hits = findPeople(v, q);
    if (hits.length === 1) return { intent: { kind: 'draft', personId: hits[0]!.id, template: 'followup_general' } };
    if (hits.length > 1) return { intent: { kind: 'unknown' }, ambiguous: hits.map((h) => h.name) };
  }
  // person-centred intents: "prepare my meeting with …", "summary of …" (name required, never guessed)
  const prep = /(prepare|prep|brief me|meeting with|προετοιμασ|συναντηση με|ραντεβου με)/.test(both);
  const summary = /(summary|summari[sz]e|overview|tell me about|who is|συνοψη|συνοψισε|πες μου για|ποιος ειναι|ποια ειναι|εικονα)/.test(both);
  if (prep || summary) {
    const hits = findPeople(v, q);
    if (hits.length === 1) return { intent: prep ? { kind: 'meeting_prep', personId: hits[0]!.id } : { kind: 'client_summary', personId: hits[0]!.id } };
    if (hits.length > 1) return { intent: { kind: 'unknown' }, ambiguous: hits.map((h) => h.name) };
  }
  if (/(opportunit|ευκαιρι|cross.?sell|upsell|what can i sell|τι να πουλησω)/.test(both)) return { intent: { kind: 'opportunities' } };
  if (/(campaign|καμπανι)/.test(both)) {
    const seg = SEGMENT_RULES.find(([re]) => re.test(both))?.[1] ?? 'parents_children_under_10';
    return { intent: { kind: 'campaign_draft', segment: seg } };
  }
  if (/(renewal|ανανεωσ)/.test(both)) {
    if (/(next week|επομενη εβδομαδα|την αλλη εβδομαδα)/.test(both)) { const f = addDays(startOfWeek(today, 1), 7); return { intent: { kind: 'renewals', from: f, to: addDays(f, 6) } }; }
    if (/(this week|αυτη την εβδομαδα|εβδομαδα)/.test(both)) { const f = startOfWeek(today, 1); return { intent: { kind: 'renewals', from: today, to: addDays(f, 6) } }; }
    return { intent: { kind: 'renewals', from: today, to: addDays(today, 30) } };
  }
  if (/(production|παραγωγ|premium written)/.test(both)) {
    const period: PeriodKind = /(today|σημερα)/.test(both) ? 'today' : /(week|εβδομαδ)/.test(both) ? 'week' : /(quarter|τριμην)/.test(both) ? 'quarter' : /(year|χρονι|ετος)/.test(both) ? 'year' : 'month';
    return { intent: { kind: 'production', period } };
  }
  if (/(who should i (call|contact)|ποιον να (παρω|καλεσω)|ποιους να|start my day|ξεκινα τη μερα)/.test(both)) return { intent: { kind: 'start_my_day' } };
  const seg = SEGMENT_RULES.find(([re]) => re.test(both))?.[1];
  if (seg) return { intent: { kind: 'segment', segment: seg } };
  return { intent: { kind: 'unknown' } };
}

export function ask(v: WorkspaceView, question: string, today: Day): AskResult {
  const { intent, ambiguous } = parseQuestion(v, question, today);
  switch (intent.kind) {
    case 'segment': {
      const r = runSegment(v, intent.segment, today);
      return { intent, answerKey: `segment.${intent.segment}`, params: { count: r.members.length, incomplete: r.incomplete.length }, people: r.members };
    }
    case 'renewals': {
      const list = renewalQueue(v, today, { includeLife: true }).filter((r) => r.dueDate >= intent.from && r.dueDate <= intent.to);
      return { intent, answerKey: 'ask.renewals', params: { count: list.length, from: intent.from, to: intent.to }, people: list.map((r) => ({ personId: r.policy.personId, detail: { product: r.policy.productName, dueDate: r.dueDate } })) };
    }
    case 'production': {
      const r = productionReport(v, intent.period, today);
      const cur = Object.entries(r.cur.byCurrency).map(([c, a]) => `${a} ${c}`).join(' · ') || '0';
      return { intent, answerKey: 'ask.production', params: { period: intent.period, total: cur, count: r.cur.count }, people: r.cur.events.filter((e) => e.personId).slice(0, 20).map((e) => ({ personId: e.personId!, detail: { type: e.type, amount: e.annualDelta, currency: e.currency, date: e.date } })) };
    }
    case 'draft': return { intent, answerKey: 'ask.draft', params: {}, people: [{ personId: intent.personId, detail: {} }] };
    case 'campaign_draft': {
      const r = runSegment(v, intent.segment, today);
      return { intent, answerKey: 'ask.campaign_draft', params: { count: r.members.length }, people: r.members };
    }
    case 'start_my_day': return { intent, answerKey: 'ask.start_my_day', params: {}, people: [] };
    case 'client_summary': {
      // only the client's own records; every figure is traceable to them
      const p = v.get('persons', intent.personId)!;
      const pols = v.where('policies', 'personId', p.id);
      const active = pols.filter(isActivePolicy);
      const annual = Math.round(active.reduce((s, x) => s + annualNow(v, x, today), 0) * 100) / 100;
      const openCases = v.where('cases', 'personId', p.id).filter((c) => !isTerminalStage(c.stage)).length;
      const openTasks = v.where('tasks', 'personId', p.id).filter((t) => t.status === 'open').length;
      const rs = reviewStatus(p, pols, today);
      const gaps = p.stage === 'client' ? coverageProfile(v, p.id, today).gaps : [];
      return { intent, answerKey: 'ask.client_summary', params: { name: p.displayName, policies: active.length, annual, cases: openCases, tasks: openTasks, review: rs.state, gaps: gaps.join(', ') || '—', health: p.stage === 'client' ? relationshipHealth(v, p.id, today).score : 0 },
        people: [{ personId: p.id, detail: { stage: p.stage, policies: active.length, annual } }] };
    }
    case 'meeting_prep': return { intent, answerKey: 'ask.meeting_prep', params: { name: v.get('persons', intent.personId)!.displayName }, people: [{ personId: intent.personId, detail: {} }] };
    case 'opportunities': {
      const items = opportunityRadar(v, today, { limit: 25 });
      return { intent, answerKey: 'ask.opportunities', params: { count: items.length }, people: items.map((x) => ({ personId: x.personId, detail: { kind: x.kind, why: x.reasonKey } })) };
    }
    default: return { intent, answerKey: ambiguous?.length ? 'ask.ambiguous' : 'ask.unknown', params: { names: (ambiguous ?? []).join(', ') }, people: [], ambiguous };
  }
}
export const ASK_EXAMPLES = ['ask.example.review', 'ask.example.education', 'ask.example.renewals', 'ask.example.life_medical', 'ask.example.draft', 'ask.example.campaign', 'ask.example.summary', 'ask.example.prep', 'ask.example.opportunities'] as const;
